/**
 * Crystal lattice atom positions in unit-cell coordinates (0..1).
 * Each lattice exposes a builder that, given an N×N×N supercell, returns the
 * absolute atom positions ready to render.
 */

export type AtomKind = {
  symbol: string;
  /** HSL string for the renderer */
  color: string;
  radius: number;
  /** Optional charge label */
  charge?: string;
};

export type LatticeAtom = {
  kind: AtomKind;
  /** Fractional coords inside the unit cell. */
  pos: [number, number, number];
};

export type LatticeBond = {
  /** Indices into the atoms array. */
  a: number;
  b: number;
};

export type Lattice = {
  key: string;
  name: string;
  type: "ionic" | "covalent" | "metallic" | "molecular";
  formula: string;
  /** Cell edge in arbitrary units (used to scale supercell). */
  a: number;
  /** Optional non-cubic c (for graphite). */
  c?: number;
  atoms: LatticeAtom[];
  /** Index pairs to draw as bonds. Use within-cell indices. */
  bonds: LatticeBond[];
  insight: string;
  property: string;
  /** Show "sea of electrons" overlay (metallic). */
  hasElectronSea?: boolean;
};

const Na: AtomKind = { symbol: "Na⁺", color: "hsl(45 95% 60%)", radius: 0.18, charge: "+" };
const Cl: AtomKind = { symbol: "Cl⁻", color: "hsl(140 60% 55%)", radius: 0.28, charge: "−" };
const C: AtomKind = { symbol: "C", color: "hsl(220 8% 30%)", radius: 0.18 };
const Cu: AtomKind = { symbol: "Cu", color: "hsl(20 75% 55%)", radius: 0.24 };
const O: AtomKind = { symbol: "O", color: "hsl(0 70% 55%)", radius: 0.2 };
const H: AtomKind = { symbol: "H", color: "hsl(0 0% 88%)", radius: 0.12 };

/** Sodium chloride: rock-salt structure. Na+ FCC, Cl- in octahedral holes. */
const NACL_ATOMS: LatticeAtom[] = [
  // Na corners + face centers
  { kind: Na, pos: [0, 0, 0] },
  { kind: Na, pos: [0.5, 0.5, 0] },
  { kind: Na, pos: [0.5, 0, 0.5] },
  { kind: Na, pos: [0, 0.5, 0.5] },
  // Cl edges + body center
  { kind: Cl, pos: [0.5, 0, 0] },
  { kind: Cl, pos: [0, 0.5, 0] },
  { kind: Cl, pos: [0, 0, 0.5] },
  { kind: Cl, pos: [0.5, 0.5, 0.5] },
];

/** Diamond cubic: 8 carbons. */
const DIAMOND_ATOMS: LatticeAtom[] = [
  // FCC
  { kind: C, pos: [0, 0, 0] },
  { kind: C, pos: [0.5, 0.5, 0] },
  { kind: C, pos: [0.5, 0, 0.5] },
  { kind: C, pos: [0, 0.5, 0.5] },
  // Tetrahedral interstitials
  { kind: C, pos: [0.25, 0.25, 0.25] },
  { kind: C, pos: [0.75, 0.75, 0.25] },
  { kind: C, pos: [0.75, 0.25, 0.75] },
  { kind: C, pos: [0.25, 0.75, 0.75] },
];

const DIAMOND_BONDS: LatticeBond[] = [
  { a: 4, b: 0 },
  { a: 4, b: 1 },
  { a: 4, b: 2 },
  { a: 4, b: 3 },
  { a: 5, b: 1 },
  { a: 6, b: 2 },
  { a: 7, b: 3 },
];

/** Graphite: hexagonal sheets at z=0 and z=0.5 (offset). */
function graphiteAtoms(): LatticeAtom[] {
  // Two hex layers, A and B (offset by half hex)
  const atoms: LatticeAtom[] = [];
  // Layer A at y=0
  const hex = [
    [0.0, 0.0],
    [0.333, 0.0],
    [0.5, 0.289],
    [0.333, 0.577],
    [0.0, 0.577],
    [-0.167, 0.289],
  ];
  for (const [x, z] of hex) {
    atoms.push({ kind: C, pos: [x + 0.25, 0.0, z + 0.2] });
  }
  // Layer B at y=0.5, offset
  for (const [x, z] of hex) {
    atoms.push({ kind: C, pos: [x + 0.4, 0.5, z + 0.2] });
  }
  return atoms;
}

const GRAPHITE_ATOMS = graphiteAtoms();
const GRAPHITE_BONDS: LatticeBond[] = [
  { a: 0, b: 1 },
  { a: 1, b: 2 },
  { a: 2, b: 3 },
  { a: 3, b: 4 },
  { a: 4, b: 5 },
  { a: 5, b: 0 },
  { a: 6, b: 7 },
  { a: 7, b: 8 },
  { a: 8, b: 9 },
  { a: 9, b: 10 },
  { a: 10, b: 11 },
  { a: 11, b: 6 },
];

/** Copper FCC. */
const CU_ATOMS: LatticeAtom[] = [
  { kind: Cu, pos: [0, 0, 0] },
  { kind: Cu, pos: [0.5, 0.5, 0] },
  { kind: Cu, pos: [0.5, 0, 0.5] },
  { kind: Cu, pos: [0, 0.5, 0.5] },
];

/** Ice Ih (simplified): O atoms in hex pattern + H atoms on bonds. */
const ICE_ATOMS: LatticeAtom[] = [
  { kind: O, pos: [0.0, 0.0, 0.0] },
  { kind: O, pos: [0.5, 0.5, 0.0] },
  { kind: O, pos: [0.5, 0.0, 0.5] },
  { kind: O, pos: [0.0, 0.5, 0.5] },
  { kind: O, pos: [0.25, 0.25, 0.25] },
  { kind: O, pos: [0.75, 0.75, 0.25] },
  { kind: O, pos: [0.75, 0.25, 0.75] },
  { kind: O, pos: [0.25, 0.75, 0.75] },
  // H atoms on selected bonds
  { kind: H, pos: [0.12, 0.12, 0.12] },
  { kind: H, pos: [0.62, 0.62, 0.12] },
  { kind: H, pos: [0.62, 0.12, 0.62] },
  { kind: H, pos: [0.12, 0.62, 0.62] },
];
const ICE_BONDS: LatticeBond[] = [
  { a: 4, b: 0 },
  { a: 4, b: 1 },
  { a: 4, b: 2 },
  { a: 4, b: 3 },
];

export const LATTICES: Lattice[] = [
  {
    key: "nacl",
    name: "Sodium chloride",
    type: "ionic",
    formula: "NaCl",
    a: 1,
    atoms: NACL_ATOMS,
    bonds: [],
    insight:
      "Each Na⁺ is surrounded by 6 Cl⁻ and vice versa. Two interpenetrating FCC lattices.",
    property:
      "Brittle. A shear that aligns same-charge ions causes catastrophic repulsion — the crystal cleaves.",
  },
  {
    key: "diamond",
    name: "Diamond",
    type: "covalent",
    formula: "C",
    a: 1,
    atoms: DIAMOND_ATOMS,
    bonds: DIAMOND_BONDS,
    insight:
      "Every carbon is sp³ — bonded to 4 neighbours in a tetrahedron throughout the crystal.",
    property:
      "Hardest natural material. Breaking the crystal means breaking strong covalent bonds in every direction.",
  },
  {
    key: "graphite",
    name: "Graphite",
    type: "covalent",
    formula: "C",
    a: 1,
    atoms: GRAPHITE_ATOMS,
    bonds: GRAPHITE_BONDS,
    insight:
      "Carbon sp² — strong covalent bonds form hexagonal sheets. Sheets stack via weak London forces.",
    property:
      "Soft and slippery (sheets slide). Conducts electricity along sheets thanks to delocalised π electrons.",
  },
  {
    key: "copper",
    name: "Copper (metallic)",
    type: "metallic",
    formula: "Cu",
    a: 1,
    atoms: CU_ATOMS,
    bonds: [],
    insight:
      "Cu²⁺ cores in an FCC lattice surrounded by a delocalised 'sea' of valence electrons.",
    property:
      "Malleable (planes slide without breaking bonds), high electrical and thermal conductivity.",
    hasElectronSea: true,
  },
  {
    key: "ice",
    name: "Ice Iₕ (water)",
    type: "molecular",
    formula: "H₂O",
    a: 1,
    atoms: ICE_ATOMS,
    bonds: ICE_BONDS,
    insight:
      "Water molecules held by hydrogen bonds in an open hexagonal cage — the open lattice is why ice is less dense than liquid water.",
    property:
      "Low melting point: H-bonds are far weaker than ionic or covalent network bonds.",
  },
];

/** Build absolute atom positions for an N×N×N supercell. */
export function buildSupercell(lattice: Lattice, n: number) {
  const out: { kind: AtomKind; pos: [number, number, number] }[] = [];
  const offset = -((n - 1) / 2);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      for (let k = 0; k < n; k++) {
        for (const atom of lattice.atoms) {
          out.push({
            kind: atom.kind,
            pos: [
              (atom.pos[0] + i + offset) * lattice.a,
              (atom.pos[1] + j + offset) * lattice.a,
              (atom.pos[2] + k + offset) * lattice.a,
            ],
          });
        }
      }
    }
  }
  return out;
}